import { useState } from 'react'
import { Controller, useController, useFormContext } from 'react-hook-form'
import { keepPreviousData } from '@tanstack/react-query'
import dayjs from 'dayjs'
import { useDebounce } from 'usehooks-ts'

import CalendarOutlined from '@ant-design/icons/CalendarOutlined'
import CloseCircleFilled from '@ant-design/icons/CloseCircleFilled'
import NumberOutlined from '@ant-design/icons/NumberOutlined'
import TagsOutlined from '@ant-design/icons/TagsOutlined'
import TeamOutlined from '@ant-design/icons/TeamOutlined'
import UnorderedListOutlined from '@ant-design/icons/UnorderedListOutlined'

import Autocomplete from '@mui/material/Autocomplete'
import Box from '@mui/material/Box'
import Checkbox from '@mui/material/Checkbox'
import Chip from '@mui/material/Chip'
import CircularProgress from '@mui/material/CircularProgress'
import MenuItem from '@mui/material/MenuItem'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import { DatePicker } from '@mui/x-date-pickers-pro'

import useLoadingState from '@shared/hooks/src/useLoadingState'
import { Lookup, useLookup } from '@shared/providers/src/DropdownOptionsProvider'
import { UserRole } from '@shared/utils'

import Avatar from '@components/Avatar'
import { useUsers } from '@components/SelectUser/SelectUser.hooks'

export default function DocumentHeader({ editable, disabled }) {
  const { control } = useFormContext()
  const policies = useLookup(Lookup.SOPPolicies)
  const categories = useLookup(Lookup.SOPCategories)
  const readOnly = !editable

  return (
    <Stack>
      <Row icon={<UnorderedListOutlined />} label="Policy Section" htmlFor="policy">
        <Controller
          name="policy"
          control={control}
          render={({ field, fieldState }) => (
            <TextField
              {...field}
              id="policy"
              select
              variant="standard"
              fullWidth
              disabled={disabled}
              error={fieldState.isTouched && Boolean(fieldState.error)}
              helperText={fieldState.isTouched ? fieldState.error?.message : ''}
              slotProps={{
                input: { disableUnderline: readOnly },
                select: {
                  readOnly,
                  displayEmpty: true,
                  IconComponent: readOnly ? NoIcon : undefined,
                  renderValue: (value) => value || <Placeholder>Select policy</Placeholder>,
                },
              }}
            >
              {policies.map((label) => (
                <MenuItem key={label} value={label}>
                  {label}
                </MenuItem>
              ))}
            </TextField>
          )}
        />
      </Row>

      <Row icon={<TagsOutlined />} label="Category" htmlFor="categories">
        <Controller
          name="categories"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              id="categories"
              select
              variant="standard"
              fullWidth
              disabled={disabled}
              slotProps={{
                input: { disableUnderline: readOnly },
                select: {
                  multiple: true,
                  readOnly,
                  displayEmpty: true,
                  IconComponent: readOnly ? NoIcon : undefined,
                  renderValue: (selected) =>
                    selected.length ? (
                      <Box sx={styles.chipWrap}>
                        {selected.map((c) => (
                          <Chip
                            key={c}
                            size="small"
                            label={c}
                            onDelete={readOnly ? undefined : () => field.onChange(selected.filter((v) => v !== c))}
                            deleteIcon={<CloseCircleFilled onMouseDown={(e) => e.stopPropagation()} />}
                          />
                        ))}
                      </Box>
                    ) : (
                      <Placeholder>Select categories</Placeholder>
                    ),
                },
              }}
            >
              {categories.map((label) => (
                <MenuItem key={label} value={label}>
                  {label}
                </MenuItem>
              ))}
            </TextField>
          )}
        />
      </Row>

      <Row icon={<NumberOutlined />} label="Version" htmlFor="version">
        <Controller
          name="version"
          control={control}
          render={({ field, fieldState }) => (
            <TextField
              {...field}
              id="version"
              variant="standard"
              fullWidth
              placeholder="—"
              disabled={disabled}
              error={fieldState.isTouched && Boolean(fieldState.error)}
              helperText={fieldState.isTouched ? fieldState.error?.message : ''}
              slotProps={{ input: { readOnly, disableUnderline: readOnly }, htmlInput: { maxLength: 10, 'data-testid': 'input-version' } }}
            />
          )}
        />
      </Row>

      <Row icon={<TeamOutlined />} label="Approvers">
        <Approvers disabled={disabled} readOnly={readOnly} />
      </Row>

      <Row icon={<CalendarOutlined />} label="Dates" labelTop>
        <Box sx={styles.dateGroup}>
          <DateField name="supersedesDate" label="Supersedes" disabled={disabled} readOnly={readOnly} />
          <DateField name="approvalDate" label="Approval" disabled={disabled} readOnly={readOnly} />
          <DateField name="originalDate" label="Original" disabled={disabled} readOnly={readOnly} />
        </Box>
      </Row>
    </Stack>
  )
}

function Row({ icon, label, htmlFor, labelTop = false, children }) {
  return (
    <Box sx={styles.row}>
      <Typography component="label" htmlFor={htmlFor} variant="body" sx={[styles.label, labelTop && { minHeight: 'auto' }]}>
        {icon}
        {label}
      </Typography>
      <Box sx={styles.value}>{children}</Box>
    </Box>
  )
}

function Placeholder({ children }) {
  return <Typography sx={{ color: 'text.disabled' }}>{children}</Typography>
}

// Caption label above the input (instead of a floating MUI label) so each date reads as a
// sub-field of the "Dates" row and matches the grey-label styling used elsewhere.
function DateField({ name, label, disabled, readOnly }) {
  const { control } = useFormContext()

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState }) => (
        <Box sx={styles.dateField}>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {label}
          </Typography>
          <DatePicker
            value={field.value}
            inputRef={field.ref}
            onChange={field.onChange}
            minDate={MIN_DATE}
            maxDate={dayjs()}
            disabled={disabled}
            readOnly={readOnly}
            slotProps={{
              textField: {
                variant: 'standard',
                fullWidth: true,
                name,
                placeholder: 'MM/DD/YYYY',
                onBlur: field.onBlur,
                error: fieldState.isTouched && Boolean(fieldState.error),
                helperText: fieldState.isTouched ? fieldState.error?.message : '',
                slotProps: { input: { disableUnderline: readOnly }, htmlInput: { 'data-testid': `date-input-${name}` } },
              },
              openPickerButton: { 'data-testid': `date-picker-${name}`, sx: readOnly ? { display: 'none' } : undefined },
            }}
          />
        </Box>
      )}
    />
  )
}

function Approvers({ disabled, readOnly }) {
  const { field, fieldState } = useController({ name: 'approvers' })
  const selected = field.value || []

  const [interacted, setInteracted] = useState(false)
  const [inputValue, setInputValue] = useState('')
  const search = useDebounce(inputValue.trim(), 300)

  const { data, isPending, isRefreshing } = useUsers(
    { search, role: UserRole.Admin, status: 'active' },
    { staleTime: 60 * 1000, placeholderData: keepPreviousData, enabled: interacted }
  )

  const loading = useLoadingState(interacted && (isPending || isRefreshing))

  // Keep already-picked approvers in the option list so they stay matched (and don't trip MUI's
  // "value not in options" warning) even when they're absent from the latest search page.
  const fetched = data || []
  const options = [...selected.filter((user) => !fetched.some((u) => u.id === user.id)), ...fetched]

  return (
    <Autocomplete
      multiple
      fullWidth
      autoComplete
      disableCloseOnSelect
      readOnly={readOnly}
      disabled={disabled}
      forcePopupIcon={false}
      value={selected}
      options={options}
      loading={loading}
      getOptionLabel={(option) => option.fullName}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      filterOptions={(x) => x}
      onFocus={() => {
        if (!readOnly && !interacted) setInteracted(true)
      }}
      onChange={(event, users) => field.onChange(users)}
      onInputChange={(event, value) => {
        if (!event || event.type === 'click') return setInputValue('')
        setInputValue(value)
      }}
      renderValue={(values, getItemProps) =>
        values.map((user, index) => {
          const { key, ...itemProps } = getItemProps({ index })
          return (
            <Chip
              key={key}
              size="small"
              avatar={<Avatar hideStatus size="xs" hover="card" user={user} />}
              label={user.fullName}
              {...itemProps}
            />
          )
        })
      }
      renderOption={({ key, ...props }, user, { selected: isSelected }) => (
        <li key={user.id} {...props} data-testid={`approver-${user.fullName}`}>
          <Checkbox sx={{ mr: 1 }} checked={isSelected} />
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center', minWidth: 0 }}>
            <Avatar hover="card" size="sm" user={user} />
            <Typography noWrap>{user.fullName}</Typography>
          </Stack>
        </li>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          variant="standard"
          placeholder={selected.length ? undefined : readOnly ? 'No approver(s)' : 'Add approver'}
          error={fieldState.isTouched && Boolean(fieldState.error)}
          helperText={fieldState.isTouched ? fieldState.error?.message : ''}
          slotProps={{
            ...params.slotProps,
            input: {
              ...params.slotProps.input,
              disableUnderline: readOnly,
              endAdornment: (
                <>
                  {loading ? <CircularProgress size={20} /> : null}
                  {params.slotProps.input.endAdornment}
                </>
              ),
            },
            htmlInput: { ...params.slotProps.htmlInput, 'data-testid': 'approver-selector-input' },
          }}
        />
      )}
    />
  )
}

const MIN_DATE = dayjs('2018-01-01')
const NoIcon = () => null

const styles = {
  row: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 1,
    minHeight: 54,
  },
  label: {
    width: 140,
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    gap: 1,
    color: 'text.secondary',
    minHeight: 27,
  },
  value: {
    flex: 1,
    minWidth: 200,
  },
  chipWrap: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 0.5,
  },
  dateGroup: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 2,
  },
  dateField: {
    display: 'flex',
    flexDirection: 'column',
    width: 150,
  },
}
